import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { Visibility, CheckCircle, Warning, ArrowBack } from '@mui/icons-material';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import DatePicker from '@Components/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import SbTextField from '@Components/Textfield';
import SbButton from '@Components/Button';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import Title from '@Components/Title';
import { EncryptedPayload } from './types';
import SBDropdown from '@Components/Dropdown';
import { getInvoiceSettings } from '@Reducers/OrganizationSetup';
import { getCarrierDetails, splitTaxOptions } from '../CreateInvoiceFlow/constant';

type Props = {
  parsedData: any[];
  onBack: () => void;
  onReset: () => void;
  carrierId: string;
  encryptedPayload: EncryptedPayload;
  discrepancyReportPayload: EncryptedPayload;
  shipmentNumbers: string[];
};

type SummaryStats = {
  totalShipments: number;
  totalCharges: number;
  totalBoxes: number;
  totalWeight: number;
  chargeTypes: Map<string, number>;
};

export default function Step3PreviewInvoice({
  carrierId,
  parsedData,
  onBack,
  onReset,
  encryptedPayload,
  discrepancyReportPayload,
  shipmentNumbers,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const carrierOptions = useSelector((state: RootStateType) => state.purchaseCarrier.carrierOptions);
  const [invoiceDate, setInvoiceDate] = useState<Dayjs | null>(dayjs());
  const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedShipment, setExpandedShipment] = useState<number | null>(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const { invoiceSetting } = useSelector((state: RootStateType) => state?.orgSetup);
  const { taxInformation = [] } = invoiceSetting || {};
  const taxInformationOptions = taxInformation.map(s => ({ display: s.display, value: s.key }));
  const [taxBreakdown, setTaxBreakdown] = React.useState<string>('');
  const [splitTax, setSplitTax] = React.useState<string>('');
  const [departmentCode, setDepartmentCode] = React.useState<string>('');
  const [locationId, setLocationId] = React.useState<string>('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const SystemList = useSelector((state: RootStateType) => state.System.data.selectedList).map(system => ({
    display: system.name,
    value: system.d365Department || '',
    id: system._id,
  }));
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);

  const activeSystemList = useMemo(() => {
    if (_systems?.length) return SystemList.filter(system => _systems.includes(system.id));
    else return [];
  }, [SystemList, _systems]);

  const carrierDetails = useMemo(() => {
    if (!carrierId) {
      return {
        d365AccountDisplayValue: '',
        vendorLocationMap: [],
      };
    }

    return getCarrierDetails(carrierOptions, carrierId);
  }, [carrierOptions, carrierId]);

  // Calculate summary statistics
  const summaryStats = useMemo((): SummaryStats => {
    const stats: SummaryStats = {
      totalShipments: parsedData.length,
      totalCharges: 0,
      totalBoxes: 0,
      totalWeight: 0,
      chargeTypes: new Map(),
    };

    parsedData.forEach(shipment => {
      stats.totalBoxes += shipment.numberOfBoxes || 0;
      stats.totalWeight += shipment.chargeableWeight || 0;

      shipment.charges.forEach((charge: any) => {
        stats.totalCharges += charge.amount || 0;
        const current = stats.chargeTypes.get(charge.name) || 0;
        stats.chargeTypes.set(charge.name, current + (charge.amount || 0));
      });
    });

    return stats;
  }, [parsedData]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Validate form
  const isFormValid = useMemo(() => {
    if (!invoiceDate || !dueDate || !invoiceNumber) return false;

    const invDate = invoiceDate.toDate();
    const dDate = dueDate.toDate();
    const today = new Date();

    return dDate >= invDate && invDate <= today;
  }, [invoiceDate, dueDate, invoiceNumber]);

  // Get validation errors
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];

    if (!invoiceNumber) errors.push('Invoice number is required');
    if (!invoiceDate) errors.push('Invoice date is required');
    if (!dueDate) errors.push('Due date is required');

    if (invoiceDate && dueDate) {
      const invDate = invoiceDate.toDate();
      const dDate = dueDate.toDate();

      if (dDate < invDate) {
        errors.push('Due date cannot be before invoice date');
      }
      if (invDate > new Date()) {
        errors.push('Invoice date cannot be in the future');
      }
    }

    return errors;
  };

  const handleSubmit = async (isD365?: boolean) => {
    if (!isFormValid) return;

    setSubmitting(true);
    try {
      const payload = {
        shipmentNumbers: shipmentNumbers,
        carrierId: carrierId,
        billDate: invoiceDate ? invoiceDate.format('YYYY-MM-DD') : null,
        dueDate: dueDate ? dueDate.format('YYYY-MM-DD') : null,
        carrierReferenceNumber: invoiceNumber.trim(),
        encryptedPayload: encryptedPayload,
        ...(discrepancyReportPayload.encrypted && { discrepancyReportPayload }),
        ...(isD365 && {
          invoiceLocation: departmentCode,
          vendorLocation: locationId,
          d365AccountDisplayValue: carrierDetails.d365AccountDisplayValue,
        }),
      };
      if (!isD365) {
        await API.post('/invoice/purchase-invoice/create-purchase-invoice', payload);
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Processing',
            subTitle: 'Your purchase invoice has been successfully sent for processing.',
            navigateTo: '/purchase-invoices',
          }),
        );
      } else {
        await API.post('/invoice/purchase-invoice/d365/create-purchase-invoice', payload);
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Processing',
            subTitle: 'Your D365 purchase invoice has been successfully sent for processing.',
            navigateTo: '/purchase-invoices',
          }),
        );
      }

      onReset();
    } catch (err: any) {
      console.error('Invoice creation failed:', err);
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: err.response.data.msg,
          subTitle: 'Please try after sometimes',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validationErrors = getValidationErrors();

  useEffect(() => {
    dispatch(getInvoiceSettings());
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Review and confirm invoice details before submission
        </Typography>
      </Box>

      {/* Invoice Details Form */}
      <Grid container spacing={{ xs: 2, md: 10 }} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Grid sx={{ marginTop: '2rem' }} container spacing={2}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Invoice Date"
                value={invoiceDate}
                handleChange={newValue => setInvoiceDate(newValue)}
                required
                error={validationErrors.find(err => err.includes('invoice date'))}
                // fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Due Date"
                value={dueDate}
                handleChange={newValue => setDueDate(newValue)}
                required
                error={validationErrors.find(err => err.includes('due date'))}
                minDate={dayjs(invoiceDate)}
              />
            </Grid>

            {/* Reference Number below */}
            <Grid item xs={12}>
              <SbTextField
                placeholder="Enter Carrier Reference Number"
                label="Carrier Reference Number"
                name="altContactName"
                onChange={e => setInvoiceNumber(e.target.value)}
                value={invoiceNumber}
                required
                // error={Boolean(showError(taxation))}
              />
            </Grid>

            <Grid item xs={12}>
              <SBDropdown
                id="departmentCode"
                value={[departmentCode]}
                label="Invoice Location"
                onChange={([value]) => {
                  if (!value) {
                    dispatch(
                      SuccessErrorModalOpen({
                        type: 'error',
                        title: 'Warning',
                        subTitle: 'Department Code is missing for this system.',
                      }),
                    );
                  }
                  setDepartmentCode(value);
                }}
                options={activeSystemList}
                placeholder="Select Department Code"
              />
            </Grid>

            <Grid item xs={12}>
              <SBDropdown
                id="locationId"
                value={[locationId]}
                label="Vendor Location"
                onChange={([value]) => {
                  setLocationId(value);
                }}
                options={
                  carrierDetails.vendorLocationMap.map(loc => ({ value: loc.locationId, display: loc.name })) || []
                }
                placeholder="Select Location"
              />
            </Grid>

            <Grid item xs={12}>
              <SBDropdown
                label="Select Taxation"
                id="taxBreakdown"
                placeholder="Select"
                options={taxInformationOptions || []}
                onChange={(value: string[]) => setTaxBreakdown(value[0] || '')}
                name="taxBreakdown"
                value={taxBreakdown ? [taxBreakdown] : []}
              />
            </Grid>
            <Grid item xs={12}>
              <SBDropdown
                label="Tax Breakdown"
                id="splitTax"
                placeholder="Select"
                options={splitTaxOptions || []}
                onChange={(value: string[]) => setSplitTax(value[0] || '')}
                name="splitTax"
                value={splitTax ? [splitTax] : []}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '1rem' }}>
            <SbButton
              variant="contained"
              size="large"
              onClick={() => handleSubmit(true)}
              disabled={!isFormValid || !locationId || !departmentCode}
              sx={{
                color: 'primary',
              }}
            >
              {submitting ? 'Submitting...' : 'Send to 365'}
            </SbButton>
          </Grid>
        </Grid>

        {/* Shipments Table */}
        <Grid item xs={12} md={8}>
          <Box>
            <Grid container justifyContent="space-between" alignItems="center" gap={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'160px'}>
                    <Title removePadding removeBorder title="Preview Invoices" titleClass="px_16-text" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" textAlign="right">
                    Total Amount - {formatCurrency(summaryStats.totalCharges)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="right" display="block">
                    Total Weight - {summaryStats.totalWeight.toFixed(1)} kg
                  </Typography>
                </Stack>
              </Stack>
            </Grid>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflowY: 'auto',
                maxHeight: 400,
              }}
            >
              <SBTable stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableHeadCell>S. No.</TableHeadCell>
                    <TableHeadCell>Shipment Number</TableHeadCell>
                    <TableHeadCell>AWB Number</TableHeadCell>
                    <TableHeadCell align="center">Boxes</TableHeadCell>
                    <TableHeadCell align="right">Ch. Weight (kg)</TableHeadCell>
                    <TableHeadCell align="right">Charges</TableHeadCell>
                    <TableHeadCell align="center">Actions</TableHeadCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {parsedData.map((shipment, index) => (
                    <React.Fragment key={index}>
                      {/* Main Row */}
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedShipment(expandedShipment === index ? null : index)}
                      >
                        <TableRowCell align="center" className="md-text uppercase">
                          {index + 1}
                        </TableRowCell>
                        <TableRowCell align="center" className="md-text uppercase">
                          {shipment.shipmentNumber || '-'}
                        </TableRowCell>
                        <TableRowCell align="center" className="md-text uppercase">
                          {shipment.awbNumber || '-'}
                        </TableRowCell>
                        <TableRowCell align="center" className="md-text uppercase">
                          {shipment.numberOfBoxes}
                        </TableRowCell>
                        <TableRowCell align="center" className="md-text uppercase">
                          {shipment.chargeableWeight}
                        </TableRowCell>
                        <TableRowCell align="center" className="md-text uppercase">
                          {formatCurrency(
                            shipment.charges.reduce((sum: number, charge: any) => sum + charge.amount, 0),
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {shipment.charges.length} charges
                          </Typography>
                        </TableRowCell>
                        <TableRowCell align="center">
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableRowCell>
                      </TableRow>

                      {/* Expanded Details Row */}
                      {expandedShipment === index && (
                        <TableRow>
                          <TableRowCell colSpan={7} sx={{ backgroundColor: 'grey.50', p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Charge Breakdown:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {shipment.charges.map((charge: any, chargeIndex: number) => (
                                <Chip
                                  key={chargeIndex}
                                  label={`${charge.name}: ${formatCurrency(charge.amount)}`}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              ))}
                            </Stack>
                          </TableRowCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </SBTable>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}
      >
        <SbButton startIcon={<ArrowBack />} onClick={onBack} variant="outlined" size="large">
          Back to Upload
        </SbButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <SbButton
            variant="contained"
            size="large"
            // startIcon={submitting ? <LinearProgress /> : <Send />}
            onClick={() => setSubmitDialogOpen(true)}
            disabled={!isFormValid || submitting}
            sx={{
              minWidth: 200,
              // background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            }}
          >
            {submitting ? 'Creating Invoice...' : 'Create Invoice'}
          </SbButton>
        </Stack>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={submitDialogOpen} onClose={() => !submitting && setSubmitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Confirm Invoice Creation
        </DialogTitle>

        <DialogContent>
          <Typography>You are about to create an invoice with the following details:</Typography>

          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Stack spacing={1}>
              <Typography>
                <strong>Carrier Reference Number:</strong> {invoiceNumber}
              </Typography>
              <Typography>
                <strong>Invoice Date:</strong> {invoiceDate ? invoiceDate.format('DD/MM/YYYY') : '-'}
              </Typography>
              <Typography>
                <strong>Due Date:</strong>
                {dueDate ? dueDate.format('DD/MM/YYYY') : '-'}
              </Typography>
              <Typography>
                <strong>Total Shipments:</strong> {summaryStats.totalShipments}
              </Typography>
              <Typography>
                <strong>Total Amount:</strong> {formatCurrency(summaryStats.totalCharges)}
              </Typography>
            </Stack>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            This action cannot be undone. Please verify all details before proceeding.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <SbButton onClick={() => setSubmitDialogOpen(false)} disabled={submitting} variant="outlined">
            Cancel
          </SbButton>
          <SbButton
            onClick={() => handleSubmit()}
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <LinearProgress /> : <CheckCircle />}
          >
            {submitting ? 'Creating...' : 'Confirm & Create'}
          </SbButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
